import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://noetvzztlqegapelaaqq.supabase.co'
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im5vZXR2enp0bHFlZ2FwZWxhYXFxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTEyODkyMzYsImV4cCI6MjA2Njg2NTIzNn0.u4j04n_fjpzR4yFecyyFe1dkgIq9ZSqWetGUkvrbhqs'

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'payfuse-web@1.0.0'
    }
  }
})

// Types pour la base de données
export interface Payment {
  id: string
  user_id: string
  amount: number
  currency: string
  provider: string
  phone: string
  status: 'pending' | 'completed' | 'failed' | 'cancelled'
  description?: string
  provider_payment_id?: string
  payment_url?: string
  webhook_url?: string
  created_at: string
  completed_at?: string
  metadata?: Record<string, any>
}

export interface Profile {
  id: string
  user_id: string
  name: string
  email: string
  role: 'admin' | 'developer' | 'merchant'
  company?: string
  phone?: string
  avatar_url?: string
  created_at: string
  updated_at: string
}

export interface Webhook {
  id: string
  user_id: string
  url: string
  events: string[]
  status: 'active' | 'inactive'
  secret?: string
  last_delivery?: string
  delivery_count: number
  failure_count: number
  created_at: string
}

export interface ApiKey {
  id: string
  user_id: string
  name: string
  key_hash: string
  key_preview: string
  permissions: string[]
  last_used?: string
  status: 'active' | 'revoked'
  created_at: string
}

// Services Supabase
export class SupabaseService {
  static async createProfile(profileData: Omit<Profile, 'id' | 'created_at' | 'updated_at'>): Promise<Profile> {
    const { data, error } = await supabase
      .from('profiles')
      .insert([{
        ...profileData,
        updated_at: new Date().toISOString()
      }])
      .select()
      .single()

    if (error) throw error
    return data
  }

  static async getProfile(userId: string): Promise<Profile | null> {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .single()

    if (error && error.code !== 'PGRST116') throw error
    return data
  }

  static async updateProfile(userId: string, updates: Partial<Profile>): Promise<Profile> {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId)
      .select()
      .single()

    if (error) throw error
    return data
  }

  static async createPayment(paymentData: Omit<Payment, 'id' | 'created_at'>): Promise<Payment> {
    const { data, error } = await supabase
      .from('payments')
      .insert([paymentData])
      .select()
      .single()

    if (error) throw error
    return data
  }

  static async getPayments(userId: string, limit: number = 50): Promise<Payment[]> {
    const { data, error } = await supabase
      .from('payments')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return data || []
  }

  static async updatePaymentStatus(
    id: string, 
    status: Payment['status'], 
    completedAt?: string
  ): Promise<Payment> {
    const updates: Partial<Payment> = { status }
    if (completedAt) updates.completed_at = completedAt

    const { data, error } = await supabase
      .from('payments')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  }

  static async createWebhook(webhookData: Omit<Webhook, 'id' | 'created_at' | 'delivery_count' | 'failure_count'>): Promise<Webhook> {
    const { data, error } = await supabase
      .from('webhooks')
      .insert([{
        ...webhookData,
        delivery_count: 0,
        failure_count: 0
      }])
      .select()
      .single()

    if (error) throw error
    return data
  }

  static async getWebhooks(userId: string): Promise<Webhook[]> {
    const { data, error } = await supabase
      .from('webhooks')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }

  static async createApiKey(apiKeyData: Omit<ApiKey, 'id' | 'created_at'>): Promise<ApiKey> {
    const { data, error } = await supabase
      .from('api_keys')
      .insert([apiKeyData])
      .select()
      .single()

    if (error) throw error
    return data
  }

  static async getApiKeys(userId: string): Promise<ApiKey[]> {
    const { data, error } = await supabase
      .from('api_keys')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }

  static subscribeToPayments(userId: string, callback: (payload: any) => void) {
    return supabase
      .channel('payments')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'payments',
        filter: `user_id=eq.${userId}`
      }, callback)
      .subscribe()
  }

  static async getAnalytics(userId: string, days: number = 30) {
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    const { data: payments, error } = await supabase
      .from('payments')
      .select('*')
      .eq('user_id', userId)
      .gte('created_at', startDate.toISOString())

    if (error) throw error

    const completedPayments = payments?.filter(p => p.status === 'completed') || []
    const totalRevenue = completedPayments.reduce((sum, p) => sum + p.amount, 0)
    const totalTransactions = completedPayments.length
    const successRate = payments?.length ? (completedPayments.length / payments.length) * 100 : 0

    // Grouper par jour
    const dailyData = this.groupPaymentsByDay(completedPayments, days)
    
    // Grouper par provider
    const providerData = this.groupPaymentsByProvider(completedPayments)

    return {
      totalRevenue,
      totalTransactions,
      successRate,
      dailyData,
      providerData,
      averageAmount: totalTransactions ? totalRevenue / totalTransactions : 0
    }
  }

  private static groupPaymentsByDay(payments: Payment[], days: number) {
    const result = []
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      const dayStart = new Date(date.setHours(0, 0, 0, 0))
      const dayEnd = new Date(date.setHours(23, 59, 59, 999))

      const dayPayments = payments.filter(p => {
        const paymentDate = new Date(p.created_at)
        return paymentDate >= dayStart && paymentDate <= dayEnd
      })

      result.push({
        name: date.toLocaleDateString('fr-FR', { weekday: 'short' }),
        transactions: dayPayments.length,
        revenue: dayPayments.reduce((sum, p) => sum + p.amount, 0)
      })
    }
    return result
  }

  private static groupPaymentsByProvider(payments: Payment[]) {
    const providerCounts = payments.reduce((acc, p) => {
      acc[p.provider] = (acc[p.provider] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const total = Object.values(providerCounts).reduce((sum, count) => sum + count, 0)
    
    if (total === 0) return []
    
    return Object.entries(providerCounts).map(([provider, count]) => ({
      name: provider,
      value: Math.round((count / total) * 100),
      amount: payments.filter(p => p.provider === provider).reduce((sum, p) => sum + p.amount, 0),
      color: this.getProviderColor(provider)
    }))
  }

  private static getProviderColor(provider: string): string {
    const colors: Record<string, string> = {
      'orange_money': '#FF6B35',
      'wave': '#4ECDC4',
      'mtn_momo': '#FFD93D',
      'airtel_money': '#E74C3C'
    }
    return colors[provider] || '#6B7280'
  }
}

// Test de connexion Supabase
export const testSupabaseConnection = async (): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('count')
      .limit(1)
    
    if (error) {
      console.error('❌ Erreur connexion Supabase:', error.message)
      return false
    }
    
    console.log('✅ Connexion Supabase réussie')
    return true
  } catch (error) {
    console.error('❌ Connexion Supabase échouée:', error)
    return false
  }
}